import { ChevronDownIcon, CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Button,
  Flex,
  FormControl,
  FormControlProps,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverContentProps,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Multiselect } from "./Multiselect";

/* eslint-disable no-bitwise */
export enum DropdownCloseBehavior {
  OnBlur = 1 << 0,
  OnSelect = 1 << 1,
  OnButton = 1 << 2,
}
/* eslint-enable no-bitwise */

interface Props<T extends ReferenceModel> {
  allowSearch?: boolean;
  isRequired?: FormControlProps["isRequired"];
  items: T[];
  maxW?: BoxProps["maxW"];
  multiple?: boolean;
  name?: string;
  onChange?: (selectedItems: T[]) => void;
  onToggle?: (item: T) => void;
  popoverWidth?: PopoverContentProps["w"];
  closeBehavior?: DropdownCloseBehavior;
  resetText?: string;
  searchPlaceholder?: string;
  showResultCount?: boolean;
  title: string;
  value: T[];
  valueDisplay?: React.ReactNode | ((selectedItems: T[]) => React.ReactNode);
  footer?: React.ReactNode;
}

export function Dropdown<T extends ReferenceModel>({
  allowSearch = false,
  isRequired = false,
  items,
  maxW,
  multiple = false,
  name,
  onChange,
  onToggle,
  popoverWidth,
  resetText = "Reset",
  searchPlaceholder = "Zoeken...",
  showResultCount = false,
  closeBehavior = multiple
    ? DropdownCloseBehavior.OnBlur
    : DropdownCloseBehavior.OnSelect | DropdownCloseBehavior.OnBlur, // eslint-disable-line no-bitwise
  title,
  value,
  valueDisplay = (n) => n.map((v) => v.name).join(", "),
  footer = null,
}: Props<T>) {
  const [searchValue, setSearchValue] = useState("");
  const { isOpen, onToggle: onPopoverToggle, onClose } = useDisclosure();
  const inputId = useId();

  /* eslint-disable no-bitwise */
  const closesOnBlur = useMemo(
    () => Boolean(closeBehavior & DropdownCloseBehavior.OnBlur),
    [closeBehavior]
  );
  const closesOnSelect = useMemo(
    () => Boolean(closeBehavior & DropdownCloseBehavior.OnSelect),
    [closeBehavior]
  );
  const closesOnButton = useMemo(
    () => Boolean(closeBehavior & DropdownCloseBehavior.OnButton),
    [closeBehavior]
  );
  /* eslint-enable no-bitwise */

  const inputName = useMemo(
    () => name ?? `dropdown-${inputId.replace(/:/g, "")}`,
    [name, inputId]
  );

  useEffect(() => {
    if (!allowSearch) {
      setSearchValue("");
    }
  }, [allowSearch]);

  const toggleItem = useCallback(
    (item: T) => {
      if (!multiple) {
        onToggle?.(item);
        onChange?.(value.includes(item) ? [] : [item]);
        if (closesOnSelect) onClose();
        return;
      }

      const selected = new Set(value.map((i) => i.id));

      if (selected.has(item.id)) {
        selected.delete(item.id);
      } else {
        selected.add(item.id);
      }

      onToggle?.(item);
      onChange?.(items.filter((i) => selected.has(i.id)));
      if (closesOnSelect) onClose();
    },
    [value, onChange, items, onToggle, closesOnSelect, multiple, onClose]
  );

  const clear = useCallback(() => {
    if (onToggle) {
      value.forEach((i) => onToggle(i));
    }
    onChange?.([]);
  }, [value, onToggle, onChange]);

  const filteredItems = useMemo(
    () =>
      items.filter((i) =>
        i.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [items, searchValue]
  );

  const DisplayValue = useCallback(() => {
    if (typeof valueDisplay === "function") {
      return <>{valueDisplay(value)}</>;
    }
    return <>{valueDisplay}</>;
  }, [valueDisplay, value]);

  return (
    <FormControl isRequired={isRequired}>
      <FormLabel>{title}</FormLabel>
      <Popover
        closeOnBlur={closesOnBlur}
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
      >
        <PopoverAnchor>
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.300"
            borderRadius={4}
            cursor="pointer"
            data-testid="dropdown-trigger"
            gap={2}
            justifyContent="space-between"
            maxW={maxW}
            mb={2}
            onClick={onPopoverToggle}
            p={2}
          >
            <DisplayValue />
            <PopoverTrigger>
              <ChevronDownIcon />
            </PopoverTrigger>
          </Flex>
        </PopoverAnchor>
        <PopoverContent borderRadius={8} w={popoverWidth}>
          <PopoverArrow />
          <PopoverHeader
            alignItems="baseline"
            as={Flex}
            border="none"
            gap={4}
            pt={4}
            px={4}
          >
            <FormLabel>{title}</FormLabel>
            <Button
              name={`reset-${inputName}`}
              onClick={clear}
              size="xs"
              variant="link"
            >
              {resetText}
            </Button>
            {closesOnButton && (
              <PopoverCloseButton color="gray.600" right={3} top={4} />
            )}
          </PopoverHeader>
          <PopoverBody>
            {allowSearch && (
              <Flex mx={1}>
                <InputGroup>
                  <InputLeftElement>
                    <SearchIcon color="gray.600" />
                  </InputLeftElement>
                  <Input
                    _placeholder={{
                      fontStyle: "italic",
                    }}
                    borderRadius="md"
                    mb={2}
                    name={`search-${inputName}`}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={searchPlaceholder}
                    value={searchValue}
                  />
                  <InputRightElement>
                    <CloseIcon
                      boxSize={2.5}
                      color="gray.600"
                      cursor="pointer"
                      name={`reset-search-${inputName}`}
                      onClick={() => setSearchValue("")}
                    />
                  </InputRightElement>
                </InputGroup>
              </Flex>
            )}
            {showResultCount && (
              <Text fontSize="xs" mx={1}>
                {filteredItems.length}{" "}
                {filteredItems.length === 1 ? "resultaat" : "resultaten"}
              </Text>
            )}
            {multiple ? (
              <Multiselect
                allItems={items}
                items={filteredItems}
                name={inputName}
                onChange={onChange}
                onToggle={onToggle}
                value={value}
              />
            ) : (
              <RadioGroup name={inputName}>
                {filteredItems.map((item) => (
                  <Box key={item.id}>
                    <Radio
                      key={item.id}
                      isChecked={value.includes(item)}
                      onChange={() => {
                        toggleItem(item);
                      }}
                      paddingInlineStart={1}
                      width="100%"
                    >
                      {item.name}
                    </Radio>
                  </Box>
                ))}
              </RadioGroup>
            )}
          </PopoverBody>
          {footer && <PopoverFooter border="none">{footer}</PopoverFooter>}
        </PopoverContent>
      </Popover>
    </FormControl>
  );
}
